import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { toast } from 'react-toastify';
import { resolveMandatoryFields, useIndustries } from 'services/industryService';
import { AccountPrice, accountPriceFor, authConfig, fetchAccountPrices, fetchPurchaseOrderVolumes, marketFor, plural, PRICES_PATH, PurchaseOrderVolume } from 'services/integrations';
import { KindLogo } from './integrationKinds';

/**
 * New Google Sheet import (`/integrations/new-sheet`), for accounts allowed to (canSellWithGSheet).
 * A Sheet's rows reach Leadrs with no page to read an activity from, so the activity and the
 * minimum price are asked first: they create an integration with a fixed activity, then its Sheet,
 * whose columns are that activity's mandatory fields. The Sheet only opens when the seller asks.
 */
const NewSheetIntegration = () => {
    const navigate = useNavigate();
    const { industries } = useIndustries();
    const [user, setUser] = useState<any>(null);
    const [volumes, setVolumes] = useState<PurchaseOrderVolume[]>([]);
    const [fieldNames, setFieldNames] = useState<Record<string, string>>({});
    const [industry, setIndustry] = useState('');
    const [subIndustry, setSubIndustry] = useState('');
    const [price, setPrice] = useState('');
    // Until the seller types a price, it follows the account price of the chosen activity (Mes prix).
    const [priceTouched, setPriceTouched] = useState(false);
    const [accountPrices, setAccountPrices] = useState<AccountPrice[]>([]);
    const [saving, setSaving] = useState(false);
    const [created, setCreated] = useState<{ templateId: string; sheetUrl: string } | null>(null);

    useEffect(() => {
        axios
            .get(`${CONFIG.apiUrl}/user/me`, authConfig())
            .then((res) => {
                setUser(res.data);
                if (!res.data?.canSellWithGSheet) navigate('/integrations/start', { replace: true });
            })
            .catch(() => {});
        fetchPurchaseOrderVolumes()
            .then(setVolumes)
            .catch(() => {});
        fetchAccountPrices()
            .then(setAccountPrices)
            .catch(() => {});
        axios
            .get(`${CONFIG.apiUrl}/form-field`)
            .then((res) => {
                const map: Record<string, string> = {};
                (res.data?.results || []).forEach((f: any) => (map[f.id] = f.name));
                setFieldNames(map);
            })
            .catch(() => {});
    }, [navigate]);

    const selectedIndustry = industries.find((i) => i.name === industry);
    const subs = selectedIndustry?.subIndustries || [];
    const columns = useMemo(
        () => resolveMandatoryFields(selectedIndustry, subIndustry).map((f) => fieldNames[f] || f),
        [selectedIndustry, subIndustry, fieldNames]
    );
    const market = marketFor(volumes, subIndustry, user);
    const accountPrice = industry ? accountPriceFor(accountPrices, industry, subIndustry) : undefined;
    useEffect(() => {
        if (!priceTouched) setPrice(accountPrice != null ? String(accountPrice) : '');
    }, [accountPrice, priceTouched]);

    const create = async () => {
        const value = parseFloat(price.replace(',', '.'));
        if (!industry) {
            toast.error("Choisissez l'activité des leads de ce Google Sheet");
            return;
        }
        if (!price || isNaN(value) || value <= 0) {
            toast.error('Indiquez un prix minimum valide');
            return;
        }
        setSaving(true);
        try {
            const template = await axios.post(
                `${CONFIG.apiUrl}/form-template`,
                { name: `Google Sheet · ${industry}`, industry, subIndustry: subIndustry || undefined, minimumPrice: value, isUniversal: false },
                authConfig()
            );
            const sheet = await axios.post(`${CONFIG.apiUrl}/form-template/${template.data._id}/get-or-create-sheet`, {}, authConfig());
            setCreated({ templateId: template.data._id, sheetUrl: sheet.data.sheetUrl });
        } catch (e: any) {
            console.error('Error creating Google Sheet integration:', e);
            toast.error(e?.response?.data?.message || 'Erreur lors de la création du Google Sheet');
        } finally {
            setSaving(false);
        }
    };

    const label = 'font-hanken text-[13.5px] font-semibold text-[#3B352C]';
    const select = 'w-full rounded-[10px] border border-[#DDD5C9] bg-white px-3 py-2.5 font-hanken text-[14px] outline-none focus:border-[#241C5C]';

    return (
        <div className="mx-auto flex max-w-[720px] flex-col gap-5 pb-16 pt-2 font-hanken text-[#1A1613]">
            <button onClick={() => navigate('/integrations/start')} className="self-start text-[13.5px] font-semibold text-[#241C5C] hover:underline">
                ← Ajouter une intégration
            </button>
            <div className="flex items-center gap-3">
                <KindLogo kind="sheet" />
                <div>
                    <h1 className="font-newsreader text-[30px] font-medium leading-tight tracking-[-0.02em] text-[#14110E]">Import Google Sheet</h1>
                    <p className="text-[14px] text-[#6B6357]">Chaque ligne que vous ajoutez au Sheet devient un lead, vendu comme les autres.</p>
                </div>
            </div>

            {created ? (
                <div className="flex flex-col gap-4 rounded-[18px] border border-[#CDEAD8] bg-[#F2FAF4] p-6">
                    <div>
                        <h2 className="font-newsreader text-[22px] font-medium text-[#14110E]">Votre Google Sheet est prêt</h2>
                        <p className="mt-1 text-[14px] text-[#3B352C]">
                            Il est partagé avec {user?.email || 'votre adresse e-mail'}. Ajoutez une ligne par lead, en remplissant chaque colonne :
                            les nouvelles lignes sont importées automatiquement, au prix minimum de {price} €.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2.5">
                        <a
                            href={created.sheetUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-[10px] bg-[#241C5C] px-4 py-2.5 text-[13.5px] font-semibold text-white hover:bg-[#332a75]"
                        >
                            Ouvrir le Google Sheet ↗
                        </a>
                        <button
                            onClick={() => navigate('/integrations')}
                            className="rounded-[10px] border border-[#DDD5C9] bg-white px-4 py-2.5 text-[13.5px] font-semibold text-[#3B352C] hover:border-[#241C5C]"
                        >
                            Retour à mes intégrations
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-5 rounded-[18px] border border-[#EDE7DE] bg-white p-6">
                    <div className="flex flex-col gap-1.5">
                        <span className={label}>1. Activité des leads</span>
                        <p className="text-[13px] text-[#8A8172]">Elle fixe les colonnes du Sheet et les acheteurs à qui les leads sont proposés.</p>
                        <div className="mt-1 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                            <select
                                value={industry}
                                onChange={(e) => {
                                    const sel = industries.find((i) => i.name === e.target.value);
                                    setIndustry(e.target.value);
                                    setSubIndustry(sel?.subIndustries?.[0]?.name || '');
                                }}
                                className={select}
                                aria-label="Activité"
                            >
                                <option value="">Choisir une activité</option>
                                {industries.map((i) => (
                                    <option key={i._id} value={i.name}>
                                        {i.name}
                                    </option>
                                ))}
                            </select>
                            {subs.length > 0 && (
                                <select value={subIndustry} onChange={(e) => setSubIndustry(e.target.value)} className={select} aria-label="Précision de l'activité">
                                    {subs.map((s: any) => (
                                        <option key={s.name} value={s.name}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        {industry && columns.length > 0 && (
                            <p className="mt-1 text-[12.5px] text-[#6B6357]">
                                <strong className="text-[#3B352C]">Colonnes du Sheet :</strong> {columns.join(' · ')}
                            </p>
                        )}
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="sheet-price" className={label}>
                            2. Prix minimum par lead
                        </label>
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="flex w-[130px] items-center rounded-[10px] border border-[#DDD5C9] bg-white">
                                <input
                                    id="sheet-price"
                                    value={price}
                                    onChange={(e) => {
                                        setPrice(e.target.value);
                                        setPriceTouched(true);
                                    }}
                                    inputMode="decimal"
                                    placeholder="15"
                                    className="w-full min-w-0 bg-transparent px-3 py-2.5 text-[14px] font-semibold outline-none"
                                />
                                <span className="pr-3 text-[#9A9184]">€</span>
                            </span>
                            {industry && (
                                <span className="text-[12.5px] text-[#6B6357]">
                                    {market.orders > 0
                                        ? `${plural(market.orders, "ordre d'achat actif", "ordres d'achat actifs")}${
                                              market.sellerPrice != null ? ` · à partir de ${market.sellerPrice} € pour vous` : ''
                                          }`
                                        : "Pas d'ordre d'achat actif pour le moment sur cette activité"}
                                </span>
                            )}
                        </div>
                        {industry && (
                            <p className="text-[12.5px] text-[#6B6357]">
                                {accountPrice != null ? (
                                    <>
                                        Votre prix du compte pour cette activité est de <strong>{accountPrice} €</strong> : il est proposé par
                                        défaut.{' '}
                                    </>
                                ) : (
                                    <>Pas encore de prix du compte pour cette activité. </>
                                )}
                                Le prix saisi ici sera propre à ce Google Sheet ·{' '}
                                <button type="button" onClick={() => navigate(PRICES_PATH)} className="font-semibold text-[#241C5C] hover:underline">
                                    Mes prix
                                </button>
                            </p>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 border-t border-[#F1ECE4] pt-4">
                        <button
                            onClick={create}
                            disabled={saving}
                            className="rounded-[10px] bg-[#241C5C] px-4 py-2.5 text-[13.5px] font-semibold text-white hover:bg-[#332a75] disabled:opacity-50"
                        >
                            {saving ? 'Création du Sheet…' : 'Créer mon Google Sheet'}
                        </button>
                        <span className="text-[12.5px] text-[#8A8172]">Le Sheet sera partagé avec votre adresse e-mail.</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewSheetIntegration;
